import { action } from '@ember/object';
import { service } from '@ember/service';
import { task } from 'ember-concurrency';
import HotelSettingsMyotbMyotbBaseController from 'lumen/controllers/hotel/settings/myotb/myotb-base';
import { STAGES } from 'lumen/utils/constants/otb-selfservice-constants';

export default class HotelSettingsMyotbPmsSetupController extends HotelSettingsMyotbMyotbBaseController {
  @service access;
  @service alerting;
  @service errorTracking;
  @service myotbSs;
  @service router;

  pageName = STAGES.PMS_SETUP_PAGE;

  setAnswerTask = task(
    { enqueue: true },
    async (setupStepName, setupStepAnswer) => {
      // // Duplicate the setup steps answers array or create a new array.
      let setupStepsAnswers = this.otbSelfService.setupStepsAnswers
        ? [...this.otbSelfService.setupStepsAnswers]
        : [];
      const setupStepsAnswer = setupStepsAnswers.find(
        (setupStepsAnswersEntry) =>
          setupStepsAnswersEntry.name === setupStepName,
      );

      // Change the answer if the value is not null, otherwise don't include the answer in the array.
      if (setupStepsAnswer) {
        const index = setupStepsAnswers.indexOf(setupStepsAnswer);

        if (setupStepAnswer !== null) {
          setupStepsAnswers[index].answer = setupStepAnswer;
        } else {
          setupStepsAnswers.splice(index, 1);
        }
      } else if (setupStepAnswer !== null) {
        setupStepsAnswers.push({
          name: setupStepName,
          answer: setupStepAnswer,
        });
      }

      // Pass null in case the array is empty because it's cleaner (the initial state in backend is also null).
      if (!setupStepsAnswers.length) {
        setupStepsAnswers = null;
      }

      // Set selected properties on the local self-service object
      this.otbSelfService.setupStepsAnswers = setupStepsAnswers;

      try {
        this.isLoading = true;
        await this.otbSelfService.save();
      } catch (error) {
        this.alerting.showErrorAlert();
        this.errorTracking.captureException(error);
      } finally {
        this.isLoading = false;
      }
    },
  );

  get isContinueEnabled() {
    // Enable the continue button if all steps are completed and the stage is STAGES.BENCHMARK_QUESTION_PAGE or STAGES.VALIDATING_DATA_PAGE.
    return (
      this.setupSteps.every((setupStep) => setupStep.answer) &&
      [STAGES.BENCHMARK_QUESTION_PAGE, STAGES.VALIDATING_DATA_PAGE].includes(
        this.otbSelfService.stage,
      )
    );
  }

  get pmsOrOtherSystemName() {
    const { otherSystem } = this.otbSelfService;

    if (otherSystem) {
      return otherSystem.otherSystemDisplayName;
    }

    return this.otbSelfService.pmsDisplayName;
  }

  get setupSteps() {
    const hasMultipleSteps =
      this.myotbSs.pmsOrOtherSystem.setupSteps.length > 1;

    return this.myotbSs.pmsOrOtherSystem.setupSteps
      .map((setupStep) => {
        const { order, name } = setupStep;
        const answer =
          this.otbSelfService.setupStepsAnswers?.find(
            (setupStepAnswer) => setupStepAnswer.name === setupStep.name,
          )?.answer ?? null;

        return {
          stepType: setupStep.step_type,
          order: hasMultipleSteps ? order : null, // Only pass the order for the step count if there are multiple steps.
          name,
          helpUrl: setupStep.help_url,
          helpText: setupStep.help_text,
          answer,
        };
      })
      .sort((stepA, stepB) => stepA.order - stepB.order);
  }

  @action
  async onClickContinue() {
    // Redirect to next page
    if (this.access.hasFeature('otb.benchmark_question.view')) {
      this.router.legacyTransitionTo('hotel.settings.myotb.benchmark');
    } else {
      this.router.legacyTransitionTo('hotel.settings.myotb.validation');
    }
  }

  @action
  onClickPrevious() {
    if (this.otbSelfService.otherSystemEligibilityQuestionsAnswers) {
      this.router.legacyTransitionTo(
        'hotel.settings.myotb.other-system-eligibility',
      );
    } else if (this.otbSelfService.otherSystem) {
      this.router.legacyTransitionTo(
        'hotel.settings.myotb.other-system-selection',
      );
    } else if (this.otbSelfService.eligibilityQuestionsAnswers) {
      this.router.legacyTransitionTo('hotel.settings.myotb.pms-eligibility');
    } else {
      this.router.legacyTransitionTo('hotel.settings.myotb.pms-selection');
    }
  }
}
